import { createContext, useContext } from "react";
import { useSkuInBoxPayment } from "./useSkuInBoxPayment";
import { useAutonomousPayment } from "./useAutonomousPayment";
import { PaymentType } from "../../api/types";
import {
  MakePaymentVariables,
  PayCheckoutVariables,
  PaymentStatusResponse,
  ZoneCartResponse,
} from "../../api/pos-api";
import { AxiosResponse } from "axios";

type MakePaymentArgs = {
  type: PaymentType;
  checkin?: PayCheckoutVariables["checkin"];
  onCheckoutCreated?: (checkoutId: string) => void;
};

export type MakePaymentFn = (args: MakePaymentArgs) => Promise<unknown>;

export type PaymentContextValue = {
  makePayment: MakePaymentFn;
  loading: PaymentType | null;
  fetchPaymentStatus: (checkoutId: string) => Promise<AxiosResponse<PaymentStatusResponse>>;
};

export const PaymentContext = createContext<PaymentContextValue>({
  makePayment: () => {
    throw Error("Component is not wrapped with PaymentContext");
  },
  loading: null,
  fetchPaymentStatus: () => {
    throw Error("Component is not wrapped with PaymentContext");
  },
});

export const usePayment = () => useContext(PaymentContext);

export const SkuInBoxPaymentProvider = ({ children, lines }: React.PropsWithChildren<{ lines: any[] }>) => {
  const value = useSkuInBoxPayment(lines);

  return <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>;
};

export const AutonomousPaymentProvider = ({
  children,
  cart,
  manualLines,
  trackCarts,
}: React.PropsWithChildren<{
  cart: ZoneCartResponse["result"]["cart"];
  manualLines: MakePaymentVariables["manual_lines"] | null;
  trackCarts: ZoneCartResponse["result"]["track_carts"];
}>) => {
  const value = useAutonomousPayment({
    cart,
    manualLines,
    trackCarts,
  });

  return <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>;
};
